import React, { useState, useEffect } from 'react';
import axios from '../api/client';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Switch, Typography, Box, Paper, IconButton,
  TextField, MenuItem, Select, FormControl, Chip, Tooltip,
  Divider, Tab, Tabs, CircularProgress, Autocomplete,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import SettingsIcon from '@mui/icons-material/Settings';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { detectRuleSetConflicts } from '../utils/GroupingRules';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8004';

const RULE_TYPE_LABELS = {
  match_all:    'Same value',
  value_in:     'Permitted values',
  max_days:     'Within X days (date spread)',
  isolate:      'Keep separate (isolate value)',
  pack_bulk_web:'Bulk + Web on same PO',
};

const newRuleDefaults = { type: 'match_all', field: '', name: '', max: 21, value: '', values: [] };

const RuleConfigDialog = ({ open, onClose, initialRuleSets, initialRules, onSave, availableFields, vendor }) => {
  const [localSets, setLocalSets]     = useState([]);
  const [activeTab, setActiveTab]     = useState(0);
  const [isAdding, setIsAdding]       = useState(false);
  const [editingRuleIdx, setEditingRuleIdx] = useState(null); // index of rule being edited, or null
  const [newRule, setNewRule]         = useState(newRuleDefaults);
  const [renamingIdx, setRenamingIdx] = useState(null);
  const [renameVal, setRenameVal]     = useState('');
  const [fieldValues, setFieldValues] = useState([]);   // unique values for selected field
  const [fieldValuesLoading, setFieldValuesLoading] = useState(false);
  const [valueSearch, setValueSearch] = useState('');   // filter text for available-values panel
  const [manualInput, setManualInput] = useState('');   // user-typed custom value

  useEffect(() => {
    if (!open) return;
    let src;
    if (initialRuleSets && initialRuleSets.length > 0) {
      src = initialRuleSets;
    } else if (initialRules && initialRules.length > 0) {
      src = [{ id: 'rs_group', name: 'Group Rules', enabled: true, rules: initialRules }];
    } else {
      // No existing rules for this vendor — start with one empty "Default" rule set.
      // Do NOT fall back to BASE_RULE_SETS: that contains AE-specific rules and
      // would silently bleed them into every other vendor the first time Configure
      // is opened and the user clicks OK.
      src = [{ id: `rs_default_${Date.now()}`, name: 'Default', enabled: true, rules: [] }];
    }
    setLocalSets(src.map(rs => ({ ...rs, rules: rs.rules.map(r => ({ ...r })) })));
    setActiveTab(0);
    setIsAdding(false);
    setEditingRuleIdx(null);
    setNewRule(newRuleDefaults);
    setRenamingIdx(null);
    setFieldValues([]);
    setValueSearch('');
    setManualInput('');
  }, [open, initialRuleSets, initialRules]);

  // Fetch unique DB values whenever user picks a field + value_in or isolate type (add or edit mode)
  useEffect(() => {
    if ((!isAdding && editingRuleIdx === null) || !['value_in', 'isolate'].includes(newRule.type) || !newRule.field || !vendor) {
      setFieldValues([]);
      return;
    }
    let cancelled = false;
    setFieldValuesLoading(true);
    setFieldValues([]);
    setValueSearch('');
    setManualInput('');
    axios.get(`${API_BASE_URL}/api/line-items/field-values`, { params: { vendor, field: newRule.field } })
      .then(res => { if (!cancelled) setFieldValues(res.data.values || []); })
      .catch(() => { if (!cancelled) setFieldValues([]); })
      .finally(() => { if (!cancelled) setFieldValuesLoading(false); });
    return () => { cancelled = true; };
  }, [newRule.field, newRule.type, isAdding, editingRuleIdx, vendor]);

  if (!open) return null;

  /* ── helpers ─────────────────────────────────────────────────────── */
  const updateSet  = (idx, patch) => setLocalSets(prev => prev.map((rs, i) => i === idx ? { ...rs, ...patch } : rs));
  const updateRule = (setIdx, ruleIdx, patch) =>
    setLocalSets(prev => prev.map((rs, i) => i !== setIdx ? rs : {
      ...rs, rules: rs.rules.map((r, j) => j !== ruleIdx ? r : { ...r, ...patch }),
    }));
  const deleteRule = (setIdx, ruleIdx) =>
    setLocalSets(prev => prev.map((rs, i) => i !== setIdx ? rs : {
      ...rs, rules: rs.rules.filter((_, j) => j !== ruleIdx),
    }));

  const currentSet = localSets[activeTab];

  const handleAddRule = () => {
    if (!newRule.field) return;
    const fLabel = availableFields?.find(f => f.key === newRule.field)?.label || newRule.field;
    let name = newRule.name.trim();
    if (!name) {
      if (newRule.type === 'match_all')  name = `Same ${fLabel}`;
      else if (newRule.type === 'max_days')  name = `${fLabel} within ${newRule.max} days`;
      else if (newRule.type === 'isolate')   name = `Isolate '${newRule.value}' in ${fLabel}`;
      else if (newRule.type === 'value_in')  name = `${fLabel} ∈ [${(newRule.values || []).length} value${(newRule.values || []).length !== 1 ? 's' : ''}]`;
    }

    if (editingRuleIdx !== null) {
      // Edit mode — patch the existing rule in place
      const existing = currentSet.rules[editingRuleIdx];
      updateRule(activeTab, editingRuleIdx, {
        field: newRule.field,
        type: newRule.type,
        name,
        ...(newRule.type === 'max_days'  && { max: newRule.max }),
        ...(newRule.type === 'isolate'   && { value: newRule.value }),
        ...(newRule.type === 'value_in'  && { values: newRule.values || [] }),
        // preserve id and enabled from the original rule
        id: existing.id,
        enabled: existing.enabled,
      });
      setEditingRuleIdx(null);
      setIsAdding(false);
      setNewRule(newRuleDefaults);
      setFieldValues([]);
      setValueSearch('');
      setManualInput('');
      return;
    }

    const rule = {
      id: `rule_custom_${Date.now()}`,
      enabled: true,
      field: newRule.field,
      type: newRule.type,
      name,
      ...(newRule.type === 'max_days'  && { max: newRule.max }),
      ...(newRule.type === 'isolate'   && { value: newRule.value }),
      ...(newRule.type === 'value_in'  && { values: newRule.values || [] }),
    };
    setLocalSets(prev => prev.map((rs, i) => i !== activeTab ? rs : { ...rs, rules: [...rs.rules, rule] }));
    setIsAdding(false);
    setNewRule(newRuleDefaults);
  };

  const handleAddRuleSet = () => {
    const newSet = {
      id: `rs_${Date.now()}`,
      name: `Rule Set ${localSets.length + 1}`,
      enabled: true,
      rules: [],
    };
    setLocalSets(prev => [...prev, newSet]);
    setActiveTab(localSets.length);
  };

  const handleDuplicateSet = () => {
    const dup = {
      ...currentSet,
      id: `rs_${Date.now()}`,
      name: `${currentSet.name} (copy)`,
      rules: currentSet.rules.map(r => ({ ...r, id: `${r.id}_${Date.now()}` })),
    };
    setLocalSets(prev => [...prev, dup]);
    setActiveTab(localSets.length);
  };

  const handleDeleteSet = () => {
    if (localSets.length <= 1) return;
    setLocalSets(prev => prev.filter((_, i) => i !== activeTab));
    setActiveTab(Math.max(0, activeTab - 1));
  };

  const handleSave = () => { onSave(localSets); onClose(); };

  /* ── rule type descriptions ──────────────────────────────────────── */
  const ruleCaption = (r) => {
    const fLabel = availableFields?.find(f => f.key === r.field)?.label || r.field || '?';
    if (r.type === 'match_all')  return `${fLabel} must be identical across all items`;
    if (r.type === 'value_in')   return `${fLabel} must be one of (OR): ${(r.values || []).join(' | ')}`;
    if (r.type === 'max_days')   return `${fLabel} spread ≤ ${r.max} days (within ${r.max} days of earliest)`;
    if (r.type === 'isolate')    return `Items with ${fLabel} = '${r.value}' stay in their own group`;
    if (r.type === 'pack_bulk_web') return 'Bulk and Web can share a PO if same PO number';
    return r.type;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { minHeight: 560 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        <SettingsIcon color="primary" />
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>Configure Grouping Rule Sets</Typography>
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
            Each rule set runs independently. Items are assigned to the first matching rule set.
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* ── Rule Set tabs ── */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', px: 2, flexShrink: 0 }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => { setActiveTab(v); setIsAdding(false); setNewRule(newRuleDefaults); }}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ flex: 1 }}
          >
            {localSets.map((rs, i) => {
              const tabConflicts = detectRuleSetConflicts(localSets).filter(
                c => c.rs1Id === rs.id || c.rs2Id === rs.id
              );
              const tabHasConflict = rs.enabled && tabConflicts.length > 0;
              return (
                <Tab
                  key={rs.id}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box
                        sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: tabHasConflict ? '#f59e0b' : rs.enabled ? '#818cf8' : 'text.disabled', flexShrink: 0 }}
                      />
                      <Typography sx={{ fontSize: '0.8rem', fontWeight: activeTab === i ? 700 : 400, color: tabHasConflict ? '#f59e0b' : 'inherit' }}>{rs.name}</Typography>
                      <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>
                        ({rs.rules.filter(r => r.enabled).length})
                      </Typography>
                      {tabHasConflict && <WarningAmberIcon sx={{ fontSize: '0.8rem', color: '#f59e0b' }} />}
                    </Box>
                  }
                />
              );
            })}
          </Tabs>
          <Tooltip title="Add rule set">
            <IconButton size="small" onClick={handleAddRuleSet} sx={{ ml: 1, color: '#818cf8' }}>
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* ── Rule set body ── */}
        {currentSet && (
          <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
            {/* Rule set header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              {renamingIdx === activeTab ? (
                <>
                  <TextField
                    size="small" autoFocus value={renameVal}
                    onChange={e => setRenameVal(e.target.value)}
                    onBlur={() => { updateSet(activeTab, { name: renameVal || currentSet.name }); setRenamingIdx(null); }}
                    onKeyDown={e => { if (e.key === 'Enter') { updateSet(activeTab, { name: renameVal || currentSet.name }); setRenamingIdx(null); } }}
                    sx={{ width: 220 }}
                  />
                </>
              ) : (
                <Typography
                  sx={{ fontWeight: 700, fontSize: '0.9375rem', cursor: 'pointer', '&:hover': { color: '#818cf8' } }}
                  onClick={() => { setRenamingIdx(activeTab); setRenameVal(currentSet.name); }}
                  title="Click to rename"
                >
                  {currentSet.name}
                </Typography>
              )}
              <Switch
                size="small"
                checked={currentSet.enabled}
                onChange={() => updateSet(activeTab, { enabled: !currentSet.enabled })}
              />
              <Typography sx={{ fontSize: '0.72rem', color: currentSet.enabled ? 'primary.main' : 'text.disabled' }}>
                {currentSet.enabled ? 'Active' : 'Disabled'}
              </Typography>
              <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
                <Tooltip title="Duplicate rule set">
                  <IconButton size="small" onClick={handleDuplicateSet}><ContentCopyIcon fontSize="small" /></IconButton>
                </Tooltip>
                <Tooltip title={localSets.length <= 1 ? 'Cannot delete last rule set' : 'Delete rule set'}>
                  <span>
                    <IconButton size="small" color="error" onClick={handleDeleteSet} disabled={localSets.length <= 1}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>

            {/* Conflict warning for this rule set */}
            {(() => {
              const conflicts = detectRuleSetConflicts(localSets).filter(
                c => (c.rs1Id === currentSet.id || c.rs2Id === currentSet.id) && currentSet.enabled
              );
              if (!conflicts.length) return null;
              return (
                <Box sx={{
                  mb: 1.5, px: 1.5, py: 1, borderRadius: '10px',
                  border: '1px solid rgba(245,158,11,0.4)',
                  bgcolor: 'rgba(245,158,11,0.07)',
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                    <WarningAmberIcon sx={{ fontSize: '1rem', color: '#f59e0b' }} />
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#f59e0b' }}>
                      Rule Set Conflict Detected
                    </Typography>
                  </Box>
                  {conflicts.map((c, ci) => {
                    const otherName = c.rs1Id === currentSet.id ? c.rs2Name : c.rs1Name;
                    return (
                      <Box key={ci}>
                        {c.fieldConflicts.map((fc, fi) => (
                          <Typography key={fi} sx={{ fontSize: '0.73rem', color: 'text.secondary', lineHeight: 1.6 }}>
                            {fc.isDuplicate
                              ? <>This rule set has <strong>identical match rules</strong> as <strong style={{ color: '#f59e0b' }}>{otherName}</strong>. Items will always be captured by whichever runs first — the second may never be reached.</>
                              : <>Overlaps with <strong style={{ color: '#f59e0b' }}>{otherName}</strong> on field <strong>{fc.field}</strong>: values [{fc.overlappingValues.slice(0, 4).join(', ')}{fc.overlappingValues.length > 4 ? `, +${fc.overlappingValues.length - 4} more` : ''}] match both rule sets. Items with these values will always go to whichever rule set runs first.</>
                            }
                          </Typography>
                        ))}
                      </Box>
                    );
                  })}
                </Box>
              );
            })()}

            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'text.secondary', mb: 1 }}>
              Rules ({currentSet.rules.length})
            </Typography>

            {currentSet.rules.length === 0 && (
              <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', borderStyle: 'dashed', mb: 1.5 }}>
                <Typography sx={{ fontSize: '0.8rem', color: 'text.disabled' }}>No rules yet — add one below.</Typography>
              </Paper>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 1.5 }}>
              {currentSet.rules.map((r, rIdx) => (
                <Paper key={r.id || rIdx} variant="outlined" sx={{
                  p: 1.25, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
                  bgcolor: r.enabled ? 'background.paper' : 'rgba(0,0,0,0.02)',
                  borderColor: r.enabled ? 'divider' : 'rgba(0,0,0,0.06)',
                }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                      <Chip
                        label={RULE_TYPE_LABELS[r.type] || r.type}
                        size="small"
                        sx={{
                          height: 18, fontSize: '0.65rem', fontWeight: 700,
                          bgcolor: r.type === 'match_all'  ? 'rgba(99,102,241,0.1)'  :
                                   r.type === 'value_in'   ? 'rgba(16,185,129,0.1)'  :
                                   r.type === 'max_days'   ? 'rgba(245,158,11,0.1)'  :
                                   'rgba(148,163,184,0.1)',
                          color:   r.type === 'match_all'  ? '#818cf8' :
                                   r.type === 'value_in'   ? '#34d399' :
                                   r.type === 'max_days'   ? '#f59e0b' :
                                   'text.secondary',
                        }}
                      />
                      <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, textDecoration: r.enabled ? 'none' : 'line-through', color: r.enabled ? 'text.primary' : 'text.disabled' }}>
                        {r.name}
                      </Typography>
                    </Box>
                    {r.type === 'value_in' && r.values?.length > 0 ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4, mt: 0.5, alignItems: 'center' }}>
                        {r.values.slice(0, 3).map((v, vi) => (
                          <React.Fragment key={v}>
                            <Tooltip title={v} placement="top" arrow>
                              <Chip
                                label={v}
                                size="small"
                                sx={{
                                  height: 18, fontSize: '0.63rem',
                                  bgcolor: 'rgba(16,185,129,0.1)', color: '#10b981',
                                  border: '1px solid rgba(16,185,129,0.3)',
                                  maxWidth: 160,
                                  '& .MuiChip-label': {
                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block',
                                  },
                                }}
                              />
                            </Tooltip>
                            {vi < Math.min(r.values.length, 3) - 1 && (
                              <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#10b981', opacity: 0.7, px: 0.2 }}>OR</Typography>
                            )}
                          </React.Fragment>
                        ))}
                        {r.values.length > 3 && (
                          <Tooltip title={r.values.slice(3).join('\n')} placement="top" arrow>
                            <Chip
                              label={`+${r.values.length - 3} more`}
                              size="small"
                              sx={{ height: 18, fontSize: '0.63rem', bgcolor: 'rgba(148,163,184,0.12)', color: 'text.secondary', cursor: 'default' }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                        {ruleCaption(r)}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', ml: 1, flexShrink: 0, gap: 0.25 }}>
                    <Switch size="small" checked={r.enabled !== false} onChange={() => updateRule(activeTab, rIdx, { enabled: !r.enabled })} />
                    {r.type === 'value_in' && (
                      <Tooltip title="Edit permitted values">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditingRuleIdx(rIdx);
                            setIsAdding(false);
                            setNewRule({
                              type: r.type,
                              field: r.field || '',
                              name: r.name || '',
                              max: r.max || 21,
                              value: r.value || '',
                              values: [...(r.values || [])],
                            });
                            setValueSearch('');
                            setManualInput('');
                          }}
                          sx={{ color: '#10b981', '&:hover': { bgcolor: 'rgba(16,185,129,0.1)' } }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <IconButton size="small" color="error" onClick={() => {
                      deleteRule(activeTab, rIdx);
                      if (editingRuleIdx === rIdx) { setEditingRuleIdx(null); setNewRule(newRuleDefaults); }
                    }} sx={{ ml: 0.25 }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Paper>
              ))}
            </Box>

            {/* ── Add / Edit rule form ── */}
            {(isAdding || editingRuleIdx !== null) ? (
              <Paper variant="outlined" sx={{
                p: 2,
                borderColor: editingRuleIdx !== null ? 'rgba(16,185,129,0.4)' : 'primary.light',
                bgcolor: editingRuleIdx !== null ? 'rgba(16,185,129,0.04)' : 'rgba(99,102,241,0.04)',
              }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.8125rem', color: editingRuleIdx !== null ? '#10b981' : 'primary.main', mb: 1.5 }}>
                  {editingRuleIdx !== null ? `Editing: "${currentSet.rules[editingRuleIdx]?.name}"` : 'New Rule'}
                </Typography>

                <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                  <Select value={newRule.type} onChange={e => {
                    const newType = e.target.value;
                    const currentFieldIsDate = availableFields?.find(f => f.key === newRule.field)?.type === 'date';
                    setNewRule({
                      ...newRuleDefaults,
                      type: newType,
                      // keep field only if switching to max_days and field is already a date field, or switching away from max_days
                      field: newType === 'max_days' ? (currentFieldIsDate ? newRule.field : '') : newRule.field,
                    });
                  }}>
                    <MenuItem value="match_all">Same value — all items must share the exact same value</MenuItem>
                    <MenuItem value="value_in">Group by permitted values — items with any of the selected values can share a group</MenuItem>
                    <MenuItem value="max_days">Within X days — date spread must not exceed X days</MenuItem>
                    <MenuItem value="isolate">Isolate — keep items with a specific value in their own group</MenuItem>
                  </Select>
                </FormControl>

                <Box sx={{ p: 1.5, bgcolor: 'background.paper', borderRadius: 1, border: '1px dashed', borderColor: 'divider', mb: 1.5 }}>
                  {/* Field selector (all types) */}
                  <FormControl fullWidth size="small" sx={{ mb: newRule.type === 'match_all' ? 0 : 1.5 }}>
                    <Select
                      value={newRule.field}
                      displayEmpty
                      onChange={e => setNewRule(p => ({ ...p, field: e.target.value }))}
                    >
                      <MenuItem value="" disabled>Select field…</MenuItem>
                      {(newRule.type === 'max_days'
                        ? availableFields?.filter(f => f.type === 'date')
                        : availableFields
                      )?.map(f => <MenuItem key={f.key} value={f.key}>{f.label}</MenuItem>)}
                    </Select>
                  </FormControl>

                  {newRule.type === 'value_in' && (
                    <Box>
                      <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 1.25 }}>
                        Items with <strong>any</strong> of the selected values can share a group.
                        Items whose value isn't in this list are excluded from this rule set.
                      </Typography>

                      {/* ── Loading ── */}
                      {fieldValuesLoading && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                          <CircularProgress size={14} />
                          <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>Loading values…</Typography>
                        </Box>
                      )}

                      {/* ── No field selected ── */}
                      {!fieldValuesLoading && !newRule.field && (
                        <Typography sx={{ fontSize: '0.72rem', color: 'text.disabled', fontStyle: 'italic' }}>
                          Select a field above to load available values.
                        </Typography>
                      )}

                      {!fieldValuesLoading && newRule.field && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>

                          {/* ── Row 1: two dropdowns side-by-side ── */}
                          <Box sx={{ display: 'flex', gap: 1.5 }}>

                            {/* Available Values — searchable Autocomplete */}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: 'text.secondary', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                Available values
                                {fieldValues.length > 0 && (
                                  <Box component="span" sx={{ ml: 0.75, fontWeight: 400, color: 'text.disabled' }}>
                                    ({fieldValues.filter(v => !(newRule.values || []).includes(String(v))).length})
                                  </Box>
                                )}
                              </Typography>
                              {fieldValues.length === 0 ? (
                                <Typography sx={{ fontSize: '0.72rem', color: 'rgba(245,158,11,0.85)', fontStyle: 'italic' }}>
                                  ⚠ No DB values found — use manual input below.
                                </Typography>
                              ) : (
                                <Autocomplete
                                  options={fieldValues.map(String).filter(v => !(newRule.values || []).includes(v))}
                                  value={null}
                                  inputValue={valueSearch}
                                  onInputChange={(_, v, reason) => { if (reason !== 'reset') setValueSearch(v); }}
                                  onChange={(_, val) => {
                                    if (val) {
                                      setNewRule(p => ({ ...p, values: [...(p.values || []), val] }));
                                      setValueSearch('');
                                    }
                                  }}
                                  blurOnSelect
                                  clearOnBlur={false}
                                  noOptionsText="No matching values"
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      size="small"
                                      placeholder="Search & select a value…"
                                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '0.78rem' } }}
                                    />
                                  )}
                                  renderOption={(props, option) => (
                                    <Box component="li" {...props} sx={{ fontSize: '0.78rem', wordBreak: 'break-word' }}>
                                      {option}
                                    </Box>
                                  )}
                                  ListboxProps={{ style: { maxHeight: 220 } }}
                                />
                              )}
                            </Box>

                            {/* Selected Values — custom dropdown list */}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#10b981', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                Selected
                                <Box component="span" sx={{ ml: 0.75, fontWeight: 400, color: 'rgba(16,185,129,0.6)' }}>
                                  ({(newRule.values || []).length})
                                </Box>
                              </Typography>
                              <Box sx={{
                                border: '1px solid',
                                borderColor: (newRule.values || []).length > 0 ? 'rgba(16,185,129,0.35)' : 'divider',
                                borderRadius: '8px',
                                minHeight: 40,
                                maxHeight: 220,
                                overflowY: 'auto',
                                bgcolor: 'rgba(0,0,0,0.015)',
                              }}>
                                {(newRule.values || []).length === 0 ? (
                                  <Typography sx={{ fontSize: '0.72rem', color: 'text.disabled', p: 1.25, fontStyle: 'italic' }}>
                                    No values selected yet
                                  </Typography>
                                ) : (
                                  (newRule.values || []).map((val, idx) => (
                                    <Box
                                      key={val}
                                      sx={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                        px: 1.25, py: 0.65,
                                        borderBottom: idx < (newRule.values || []).length - 1 ? '1px solid' : 'none',
                                        borderColor: 'rgba(16,185,129,0.12)',
                                        '&:hover': { bgcolor: 'rgba(16,185,129,0.05)' },
                                        transition: 'background 0.1s',
                                      }}
                                    >
                                      <Typography sx={{ fontSize: '0.78rem', color: '#10b981', fontWeight: 500, wordBreak: 'break-word', flex: 1, mr: 0.5, lineHeight: 1.3 }}>
                                        {val}
                                      </Typography>
                                      <Tooltip title="Remove" placement="left" arrow>
                                        <IconButton
                                          size="small"
                                          onClick={() => setNewRule(p => ({ ...p, values: p.values.filter(v => v !== val) }))}
                                          sx={{ p: 0.3, flexShrink: 0, color: 'text.disabled', borderRadius: '5px', '&:hover': { color: '#ef4444', bgcolor: 'rgba(239,68,68,0.08)' } }}
                                        >
                                          ×
                                        </IconButton>
                                      </Tooltip>
                                    </Box>
                                  ))
                                )}
                              </Box>
                            </Box>
                          </Box>

                          {/* ── Row 2: Manual / custom value input ── */}
                          <Box>
                            <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: 'text.secondary', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                              Custom value
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.75 }}>
                              <TextField
                                size="small" fullWidth
                                placeholder="Type any value not in the list above…"
                                value={manualInput}
                                onChange={e => setManualInput(e.target.value)}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') {
                                    const v = manualInput.trim();
                                    if (v && !(newRule.values || []).includes(v)) {
                                      setNewRule(p => ({ ...p, values: [...(p.values || []), v] }));
                                    }
                                    setManualInput('');
                                  }
                                }}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '0.78rem' } }}
                              />
                              <Button
                                size="small" variant="outlined"
                                disabled={!manualInput.trim() || (newRule.values || []).includes(manualInput.trim())}
                                onClick={() => {
                                  const v = manualInput.trim();
                                  if (v && !(newRule.values || []).includes(v)) {
                                    setNewRule(p => ({ ...p, values: [...(p.values || []), v] }));
                                  }
                                  setManualInput('');
                                }}
                                sx={{ flexShrink: 0, fontSize: '0.75rem', borderRadius: '8px', px: 1.5 }}
                              >
                                Add
                              </Button>
                            </Box>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  )}

                  {newRule.type === 'max_days' && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography sx={{ fontSize: '0.8rem' }}>Date spread must not exceed</Typography>
                      <TextField
                        size="small" type="number" sx={{ width: 80 }}
                        value={newRule.max}
                        onChange={e => setNewRule(p => ({ ...p, max: parseInt(e.target.value) || 0 }))}
                      />
                      <Typography sx={{ fontSize: '0.8rem' }}>days (= within that many days of earliest date in group)</Typography>
                    </Box>
                  )}

                  {newRule.type === 'isolate' && (
                    <Box>
                      <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 0.75 }}>
                        Items whose field value matches the selected value will be kept in their own separate group.
                      </Typography>
                      {/* Loading */}
                      {fieldValuesLoading && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                          <CircularProgress size={14} />
                          <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>Loading values from database…</Typography>
                        </Box>
                      )}
                      {/* Chip picker — single select */}
                      {!fieldValuesLoading && newRule.field && fieldValues.length > 0 && (
                        <Box>
                          <Typography sx={{ fontSize: '0.68rem', color: 'text.disabled', mb: 0.5 }}>
                            Click to select the value to isolate:
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {fieldValues.map(val => {
                              const selected = newRule.value === String(val);
                              return (
                                <Chip
                                  key={String(val)}
                                  label={String(val)}
                                  size="small"
                                  clickable
                                  onClick={() => setNewRule(p => ({ ...p, value: selected ? '' : String(val) }))}
                                  sx={{
                                    height: 24, fontSize: '0.73rem',
                                    bgcolor: selected ? 'rgba(99,102,241,0.15)' : 'rgba(0,0,0,0.04)',
                                    color: selected ? '#818cf8' : 'text.secondary',
                                    border: '1px solid',
                                    borderColor: selected ? 'rgba(99,102,241,0.5)' : 'divider',
                                    fontWeight: selected ? 700 : 400,
                                    '&:hover': { bgcolor: selected ? 'rgba(99,102,241,0.22)' : 'rgba(0,0,0,0.08)' },
                                  }}
                                />
                              );
                            })}
                          </Box>
                          {newRule.value && (
                            <Typography sx={{ fontSize: '0.7rem', color: '#818cf8', mt: 0.5, fontWeight: 600 }}>
                              ✓ Will isolate: "{newRule.value}"
                            </Typography>
                          )}
                        </Box>
                      )}
                      {/* No field selected */}
                      {!fieldValuesLoading && !newRule.field && (
                        <Typography sx={{ fontSize: '0.72rem', color: 'text.disabled', fontStyle: 'italic' }}>
                          Select a field above to see available values.
                        </Typography>
                      )}
                      {/* No values in DB */}
                      {!fieldValuesLoading && newRule.field && fieldValues.length === 0 && (
                        <Typography sx={{ fontSize: '0.72rem', color: 'rgba(245,158,11,0.9)' }}>
                          ⚠ No values found in the database for this field under the selected customer.
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>

                <TextField
                  fullWidth size="small" label="Rule name (optional — auto-generated if blank)"
                  value={newRule.name}
                  onChange={e => setNewRule(p => ({ ...p, name: e.target.value }))}
                  sx={{ mb: 1.5 }}
                />

                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button size="small" onClick={() => {
                    setIsAdding(false);
                    setEditingRuleIdx(null);
                    setNewRule(newRuleDefaults);
                    setFieldValues([]);
                    setValueSearch('');
                    setManualInput('');
                  }}>Cancel</Button>
                  <Button
                    size="small" variant="contained" onClick={handleAddRule}
                    disabled={
                      !newRule.field ||
                      (newRule.type === 'isolate'  && !newRule.value.trim()) ||
                      (newRule.type === 'value_in' && !(newRule.values || []).length)
                    }
                    sx={editingRuleIdx !== null ? {
                      background: 'linear-gradient(135deg, #10b981, #059669)',
                      '&:hover': { background: 'linear-gradient(135deg, #059669, #047857)' },
                    } : {}}
                  >
                    {editingRuleIdx !== null ? 'Save Changes' : 'Add Rule'}
                  </Button>
                </Box>
              </Paper>
            ) : (
              <Button startIcon={<AddIcon />} size="small" onClick={() => { setIsAdding(true); setEditingRuleIdx(null); }}>
                Add Rule to "{currentSet.name}"
              </Button>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Typography sx={{ flex: 1, fontSize: '0.75rem', color: 'text.secondary' }}>
          Rule sets are saved per customer and reused every time you fetch.
        </Typography>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} sx={{ background: 'linear-gradient(135deg,#6366f1,#818cf8)' }}>
          Save Rule Sets
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RuleConfigDialog;
